import React, {
  FunctionComponent,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Icon,
  Popover,
  PopOverDirections,
  WithPopover,
} from '@lightspeed/design-system-react'
import classNames from 'classnames'

export interface DelayedTooltipProps {
  canBeShown?: boolean
  direction: PopOverDirections
  header?: string
  icon?: string
  insideModal?: boolean
  message: React.ReactNode
  className?: string
  openDelay?: number
  closeDelay?: number
  children: ReactElement
}

export const DelayedTooltip: FunctionComponent<DelayedTooltipProps> = ({
  canBeShown = true,
  direction,
  header,
  icon,
  insideModal,
  message,
  className,
  openDelay = 300,
  closeDelay = 150,
  children,
}) => {
  const [isMounted, setIsMounted] = useState(false)
  const [isExiting, setIsExiting] = useState(false)

  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(false)

  const clearOpenTimer = useCallback(() => {
    if (!openTimerRef.current) return

    clearTimeout(openTimerRef.current)
    openTimerRef.current = null
  }, [])

  const clearCloseTimer = useCallback(() => {
    if (!closeTimerRef.current) return

    clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }, [])

  const clearAllTimers = useCallback(() => {
    clearOpenTimer()
    clearCloseTimer()
  }, [clearOpenTimer, clearCloseTimer])

  const setMountedState = useCallback((mounted: boolean) => {
    isMountedRef.current = mounted
    setIsMounted(mounted)
  }, [])

  const hasContent = Boolean(header || message === 0 || message)

  const handleOpen = useCallback(() => {
    clearCloseTimer()

    if (!canBeShown || !hasContent) {
      return
    }

    if (isMountedRef.current) {
      setIsExiting(false)
      clearOpenTimer()
      return
    }

    clearOpenTimer()
    openTimerRef.current = setTimeout(() => {
      setMountedState(true)
      setIsExiting(false)
      openTimerRef.current = null
    }, openDelay)
  }, [
    canBeShown,
    clearCloseTimer,
    clearOpenTimer,
    hasContent,
    openDelay,
    setMountedState,
  ])

  const handleClose = useCallback(() => {
    clearOpenTimer()

    if (!isMountedRef.current) return

    setIsExiting(true)
    clearCloseTimer()
    closeTimerRef.current = setTimeout(() => {
      setMountedState(false)
      setIsExiting(false)
      closeTimerRef.current = null
    }, closeDelay)
  }, [clearCloseTimer, clearOpenTimer, closeDelay, setMountedState])

  const handleImmediateDismiss = useCallback(() => {
    clearAllTimers()
    setIsExiting(false)
    setMountedState(false)
  }, [clearAllTimers, setMountedState])

  const handleMouseOver = useCallback(
    (e: React.MouseEvent) => {
      const isEnteringFromOutside = !e.currentTarget.contains(
        e.relatedTarget as Node
      )
      if (isEnteringFromOutside) {
        handleOpen()
      }
    },
    [handleOpen]
  )

  const handleMouseOut = useCallback(
    (e: React.MouseEvent) => {
      const isLeavingToOutside = !e.currentTarget.contains(
        e.relatedTarget as Node
      )
      if (isLeavingToOutside) {
        handleClose()
      }
    },
    [handleClose]
  )

  // Cleanup delayed tooltip timers on unmount
  useEffect(() => {
    return () => {
      clearAllTimers()
    }
  }, [clearAllTimers])

  // Dismiss tooltip immediately if canBeShown becomes false
  useEffect(() => {
    if (!canBeShown) {
      handleImmediateDismiss()
    }
  }, [canBeShown, handleImmediateDismiss])

  const child = React.Children.only(children)

  const childrenWithBoundEvents = React.cloneElement(child, {
    onMouseOver: (e: React.MouseEvent) => {
      child.props.onMouseOver?.(e)
      handleMouseOver(e)
    },
    onMouseOut: (e: React.MouseEvent) => {
      child.props.onMouseOut?.(e)
      handleMouseOut(e)
    },
    onFocus: (e: React.FocusEvent) => {
      child.props.onFocus?.(e)
      handleOpen()
    },
    onBlur: (e: React.FocusEvent) => {
      child.props.onBlur?.(e)
      handleClose()
    },
    onClick: (e: React.MouseEvent) => {
      child.props.onClick?.(e)
      handleImmediateDismiss()
    },
  })

  return (
    <WithPopover direction={direction} insideModal={insideModal}>
      {childrenWithBoundEvents}
      {canBeShown && hasContent && isMounted && (
        <Popover
          className={classNames(
            'vd-popover--tooltip',
            'delayed-tooltip',
            {
              'delayed-tooltip--exit': isExiting,
            },
            className
          )}
          onClose={handleClose}
        >
          {header && (
            <p className="vd-text-label vd-mb3">
              {icon && <Icon icon={icon} className="vd-mr1" />}
              {header}
            </p>
          )}
          {(message === 0 || message) && <div className="vd-p">{message}</div>}
        </Popover>
      )}
    </WithPopover>
  )
}
